import React, { useEffect, useRef, useState } from 'react';
import { formatUserFacingError } from '@/utils/userFacingErrors';

type CertificateMatch = {
  CertificateId: number;
  CertificateNumber?: string;
  ProjectNumber?: string;
  ProjectLocation?: string;
  CertificateType?: string;
  IssueDate?: string;
  Status?: string;
};

type Props = {
  certificateId: string;
  loading: boolean;
  onSelect: (certificateId: string) => Promise<void>;
  onQueryChange: () => void;
};

export default function CertificateHistoryLookup({ certificateId, loading, onSelect, onQueryChange }: Props) {
  const [query, setQuery] = useState(certificateId);
  const [matches, setMatches] = useState<CertificateMatch[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    requestId.current += 1;
    setQuery(certificateId);
    setMatches(null);
    setSearching(false);
    setError('');
  }, [certificateId]);

  useEffect(() => () => { requestId.current += 1; }, []);

  const search = async () => {
    const value = query.trim();
    if (!value) return;
    const currentRequest = ++requestId.current;
    setSearching(true);
    setError('');
    setMatches(null);
    onQueryChange();
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const rows = await azureSqlService.getCertificates({
        page: 1, pageSize: 200, query: { q: value }, useCache: false,
      });
      if (requestId.current !== currentRequest) return;
      setMatches(Array.isArray(rows) ? rows : []);
    } catch (err) {
      if (requestId.current === currentRequest) {
        setError(formatUserFacingError(err, 'Certificates could not be searched. Please try again.'));
      }
    } finally {
      if (requestId.current === currentRequest) setSearching(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
      <label htmlFor="certificate-history-search" style={{ fontSize: 13, fontWeight: 700 }}>
        Certificate ID, certificate number or project number
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <input
          id="certificate-history-search"
          placeholder="e.g. 45 or 25070"
          value={query}
          disabled={loading}
          onChange={(event) => {
            requestId.current += 1;
            setQuery(event.target.value);
            setMatches(null);
            setSearching(false);
            setError('');
            onQueryChange();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void search();
            }
          }}
          style={{ flex: '1 1 180px', minWidth: 0, border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px', fontSize: 14 }}
        />
        <button
          type="button"
          disabled={searching || loading || !query.trim()}
          onClick={() => { void search(); }}
          style={{ border: 'none', borderRadius: 10, padding: '10px 16px', background: '#0f172a', color: '#fff', cursor: 'pointer' }}
        >
          {searching ? 'Searching...' : 'Search'}
        </button>
      </div>
      {error ? <div role="alert" style={{ color: '#b91c1c' }}>{error}</div> : null}
      {matches ? (
        <div aria-label="Matching certificates" style={{ display: 'grid', gap: 6, maxHeight: 260, overflow: 'auto' }}>
          <div role="status" style={{ fontSize: 13, color: '#475569' }}>
            {matches.length ? 'Choose a certificate to view its history.' : 'No matching certificates found.'}
            {matches.length === 200 ? ' Showing up to 200 matches; refine your search for more specific results.' : ''}
          </div>
          {matches.map((certificate) => (
            <button
              key={certificate.CertificateId}
              type="button"
              disabled={loading}
              onClick={() => { void onSelect(String(certificate.CertificateId)); }}
              style={{ textAlign: 'left', padding: '10px 12px', background: '#f8fafc', border: '1px solid #dbe4f0', borderRadius: 10, cursor: 'pointer', overflowWrap: 'anywhere' }}
            >
              <div style={{ fontWeight: 700 }}>{certificate.CertificateNumber || `Certificate ${certificate.CertificateId}`}</div>
              <div style={{ marginTop: 4, fontSize: 12, color: '#475569' }}>
                {[
                  `ID ${certificate.CertificateId}`,
                  certificate.ProjectNumber ? `Project ${certificate.ProjectNumber}` : '',
                  certificate.ProjectLocation,
                  certificate.CertificateType,
                  certificate.Status,
                  certificate.IssueDate ? String(certificate.IssueDate).slice(0, 10) : '',
                ].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
